import React, { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import QrScanner from 'qr-scanner'

import { BaseView } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { AppIcons } from '@/components/AppIcons'
import { ModalRequiredPermission } from '@/components/ModalRequiredPermission'
import { useCameraPermissionHandler } from '@/hooks/useCameraPermissionHandler'

interface ScanTemplateProps {
  onBarcodeScanned: (scanningResult: string) => void
  onRejectAccessCameraPermission: () => void
}

export const ScanTemplate: React.FC<ScanTemplateProps> = ({
  onBarcodeScanned,
  onRejectAccessCameraPermission,
}) => {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const qrScannerRef = useRef<QrScanner | null>(null)
  const lastScannedTimestampRef = useRef(0)

  const [isTorchOn, setIsTorchOn] = useState(false)
  const [isCameraReady, setIsCameraReady] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)
  const [isTogglingTorch, setIsTogglingTorch] = useState(false)

  const {
    isPermissionGranted,
    openRequiredPermissionModal,
    permissionError,
    handleCancelRequiredPermissionModal,
    handleRetryPermission,
  } = useCameraPermissionHandler({ onRejectAccessCameraPermission })

  // Initialize QR scanner when permission is granted
  useEffect(() => {
    if (!isPermissionGranted || !videoRef.current) return

    const initializeScanner = async () => {
      try {
        const qrScanner = new QrScanner(
          videoRef.current!,
          (result) => handleScan(result.data),
          {
            highlightScanRegion: false,
            highlightCodeOutline: false,
            preferredCamera: 'environment', // Back camera
            maxScansPerSecond: 2,
            // Ограничиваем область сканирования
            calculateScanRegion: (video) => {
              const videoWidth = video.videoWidth
              const videoHeight = video.videoHeight

              // Размеры области сканирования (300x300px)
              const scanSize = 300

              // Позиция области сканирования (35% от верха экрана)
              const topPercent = 0.35
              const leftPercent = 0.5

              // Вычисляем координаты в пикселях видео
              const scanRegionSize = Math.min(videoWidth, videoHeight) * 0.4 // 40% от размера видео
              const x = videoWidth * leftPercent - scanRegionSize / 2
              const y = videoHeight * topPercent - scanRegionSize / 2

              return {
                x: Math.max(0, x),
                y: Math.max(0, y),
                width: Math.min(scanRegionSize, videoWidth),
                height: Math.min(scanRegionSize, videoHeight),
              }
            },
          },
        )

        qrScannerRef.current = qrScanner

        // Start scanner first
        await qrScanner.start()
        setIsCameraReady(true)

        // Check if torch is supported after scanner is started
        try {
          // Check if flash is available on the current camera
          const hasFlash = await qrScanner.hasFlash()
          setTorchSupported(hasFlash)
        } catch (error) {
          setTorchSupported(false)
        }
      } catch (error) {
        toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
      }
    }

    initializeScanner()

    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.destroy()
        qrScannerRef.current = null
      }
    }
  }, [isPermissionGranted, t])

  const handleScan = (scanningResult: string) => {
    const timestamp = Date.now()

    // Prevent multiple scans in rapid succession (debounce 2 seconds)
    const scannedTooSoon = timestamp - lastScannedTimestampRef.current < 2000
    if (scannedTooSoon) {
      return
    }

    lastScannedTimestampRef.current = timestamp

    if (typeof scanningResult !== 'string' || !scanningResult.trim()) {
      return toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
    }

    // Disable torch before redirect
    if (isTorchOn && qrScannerRef.current) {
      qrScannerRef.current.turnFlashOff().catch(() => {})
      setIsTorchOn(false)
    }

    // Wait a bit before calling the callback to ensure torch is off
    setTimeout(() => onBarcodeScanned(scanningResult.trim()), 100)
  }

  const toggleTorch = async () => {
    if (
      !qrScannerRef.current ||
      !torchSupported ||
      !isCameraReady ||
      isTogglingTorch
    ) {
      return
    }

    setIsTogglingTorch(true)
    const currentScanner = qrScannerRef.current

    try {
      if (isTorchOn) {
        // Update UI immediately for better UX
        setIsTorchOn(false)

        // Turn off flash with minimal delay
        try {
          await currentScanner.turnFlashOff()
        } catch (error) {
          // If turning off fails, ensure state is consistent
          setIsTorchOn(false)
        }
      } else {
        // Turn on flash
        try {
          await currentScanner.turnFlashOn()
          setIsTorchOn(true)
        } catch (error) {
          setIsTorchOn(false)
        }
      }
    } catch (error) {
      setIsTorchOn(false)
    } finally {
      // Small delay before allowing next toggle
      setTimeout(() => {
        setIsTogglingTorch(false)
      }, 200)
    }
  }

  return (
    <>
      {/* Camera View */}
      {isPermissionGranted && (
        <BaseView className="fixed inset-0 bg-black">
          {/* Video element for camera feed */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Overlay с маской для области сканирования */}
          <div
            className="absolute inset-0"
            style={{
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
              top: 'calc(35% - 150px)',
              left: 'calc(50% - 150px)',
              width: '300px',
              height: '300px',
              borderRadius: '24px',
            }}
          />

          {/* Рамка области сканирования */}
          <div
            className="absolute"
            style={{
              top: 'calc(35% - 150px)',
              left: 'calc(50% - 150px)',
              width: '300px',
              height: '300px',
              borderRadius: '24px',
              // border: '2px solid white',
            }}
          >
            {/* Corner indicators */}
            {/* <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-lg" />
            <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-lg" />
            <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-lg" />
            <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-lg" /> */}
          </div>

          {/* Torch button - positioned under scan area */}
          {torchSupported && (
            <BaseView
              className="absolute left-1/2 transform -translate-x-1/2"
              style={{
                top: 'calc(35% + 170px)', // Positioned right under the scan area (300px height + 20px margin)
              }}
            >
              <button
                onClick={toggleTorch}
                disabled={!isCameraReady || isTogglingTorch}
                className={`
                  w-16 h-16 rounded-full flex items-center justify-center
                  transition-all duration-200 hover:opacity-80
                  ${
                    isTorchOn
                      ? 'bg-white bg-opacity-20 border border-white'
                      : 'bg-black bg-opacity-40 border border-gray-400'
                  }
                  ${!isCameraReady || isTogglingTorch ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {!isCameraReady || isTogglingTorch ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                ) : (
                  <AppIcons
                    name="flash"
                    size={24}
                    color={isTorchOn ? 'white' : '#9CA3AF'}
                  />
                )}
              </button>
            </BaseView>
          )}
        </BaseView>
      )}

      {/* Permission Modal */}
      <ModalRequiredPermission
        title={t('screens:modal.requiredPermission', 'Permission Required')}
        description={
          permissionError ||
          t(
            'modal.cameraPermission',
            'Camera access is required to scan QR codes',
          )
        }
        isVisible={openRequiredPermissionModal}
        onConfirm={handleRetryPermission}
        onCancel={handleCancelRequiredPermissionModal}
        confirmText={t('form:actions.retry', 'Retry')}
        cancelText={t('form:actions.cancel', 'Cancel')}
      />
    </>
  )
}
